import React, { createContext, useRef, useState } from "react";
import CryptoJS from "crypto-js";
import * as Sentry from "@sentry/react";
import ConstConfig from "../configs/constConfig";
import Ringtone from "../../../src/component/audio/ringtone.wav";
const AuthContext = createContext(null);
const { Provider } = AuthContext;

const AuthProvider = ({ children }) => {
  const isOnServer = process.env.REACT_APP_ON_SERVER == "true";

  // Add auth initialization loading state
  const [authLoading, setAuthLoading] = useState(true);

  const [user, setUser] = useState({
    access_token: null,
    details: null,
  });

  const [authUserRole, setAuthRole] = useState(null);
  const [agoraAppId, setAgoraAppId] = useState("");
  const [appleClientId, setAppleClientId] = useState("");
  const [facebookAppId, setFacebookAppId] = useState("");
  const [google_client_id, setGoogleClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [agoraMessage, setAgoraMessage] = useState({});
  const [agoraNewMessage, setAgoraNewMessage] = useState([]);
  const [socket, setSocket] = useState(null);
  const [acceptThroughPopup, setAcceptThroughPopup] = useState(false);

  const selectedChat_user = useRef(null);
  const app_js_intialLoad = useRef(false);
  const footer_ref = useRef(null);

  // Ref to store end call functions from Chat.js
  const endCallFunctionRef = useRef(null);
  // Ref to store minimize function from Chat.js
  const minimizeFunctionRef = useRef(null);

  // Ref to store call duration (prevents re-renders)
  const callDurationRef = useRef(0);
  const [selectedUser, setSelectedUser] = useState({});
  const [refreshAppointmentType, setRefreshAppointmentType] = useState({
    type: "",
    version: "",
  });

  const [banner_details, setBannerDetails] = useState([]);

  const [sessionModal, setSessionModalShow] = useState(false);
  const [showTimezoneChangeAlert, setShowTimezoneChangeAlert] = useState(false);

  const [show_otp_modal, setShowOtpModal] = useState(false);
  const [showOldLicenseModal, setShowOldLicenseModal] = React.useState(false);

  // State to track if user is in a call
  const [isInCall, setIsInCall] = useState(false);

  // State to track if call is minimized (floating window)
  const [isCallMinimized, setIsCallMinimized] = useState(false);

  // Call metadata for PiP window
  const [callMetadata, setCallMetadata] = useState({
    callType: "", // "audio" or "video"
    sendingCall: false,
    receivingCall: false,
    mute_audio: false,
    hide_video: false,
    showMessage: "",
    callDuration: 0, // Call duration in seconds
    localVideoTrack: null,
    remoteVideoTrack: null,
    localAudioTrack: null,
    remoteAudioTrack: null,
  });

  // NEW: Unified call state - Single source of truth for all call data
  const [unifiedCallState, setUnifiedCallState] = useState({
    // Call status tracking
    status: 'idle', // 'idle' | 'ringing_out' | 'ringing_in' | 'connecting' | 'active' | 'ending' | 'ended'
    type: null,     // null | 'audio' | 'video'
    role: null,     // null | 'caller' | 'callee'

    // User context
    chat_id: null,
    remoteUserId: null,
    user: {
      _id: null,
      first_name: '',
      last_name: '',
      profile_image: '',
    },

    // Appointment data (for session expiry tracking)
    appointment: {
      _id: null,
      start_date_time: null,
      end_date_time: null,
      session_time: null,
    },

    // Call metadata
    startTime: null,
    duration: 0,
    message_id: null,

    // UI states
    isMinimized: false,
    isMuted: false,
    isVideoHidden: false,
    showMessage: '',

    // Agora tracks
    tracks: {
      localAudio: null,
      localVideo: null,
      remoteAudio: null,
      remoteVideo: null,
    },

    // Loading states
    isLoading: false,
    isEnding: false,

    // Multi-tab tracking
    activeTabId: null,

    // Session expiry tracking
    expiry: {
      warningShown: false,
      expiredNotified: false,
      isExpired: false,
    },
  });

  // Initialize auth data from localStorage on mount
  React.useEffect(() => {
    try {
      const token = JSON.parse(decryptData("E_C_U_A_T"));
      const details = JSON.parse(decryptData("E_C_U_D"));
      const role = JSON.parse(decryptData("E_C_R_O_L_E"));

      setUser({
        access_token: token,
        details: details,
      });
      setAuthRole(role);

      // Set Sentry user context for error tracking
      if (token && details) {
        Sentry.setUser({
          id: details._id,
          email: details.email,
          username: details.first_name ? `${details.first_name} ${details.last_name || ""}`.trim() : undefined,
        });
      }
    } catch (error) {
      console.error("Error loading auth data:", error);
    } finally {
      // Set auth loading to false after checking localStorage
      setAuthLoading(false);
    }
  }, []);

  // GLOBAL CALL DURATION TIMER (using ref to prevent re-renders)
  // Tracks call duration globally - continues counting during navigation and PiP
  React.useEffect(() => {
    let durationTimer = null;

    console.log('🕐 Timer Effect - Status:', unifiedCallState.status, 'Ref Duration:', callDurationRef.current);

    if (unifiedCallState.status === 'active') {
      // Call is active - start/continue the timer
      console.log('▶️ Starting global duration timer (ref-based)');
      durationTimer = setInterval(() => {
        // Update ref only - does NOT cause context re-render
        callDurationRef.current = callDurationRef.current + 1;
      }, 1000);
    } else if (unifiedCallState.status === 'idle' || unifiedCallState.status === 'ended') {
      // Call ended - reset duration ref
      console.log('⏹️ Resetting duration ref - status:', unifiedCallState.status);
      callDurationRef.current = 0;
    } else {
      console.log('⏸️ Timer paused - status:', unifiedCallState.status);
    }
    // For 'ringing_out', 'ringing_in', 'connecting' - timer stops but doesn't reset

    // Cleanup timer on unmount or status change
    return () => {
      if (durationTimer) {
        console.log('🛑 Clearing duration timer');
        clearInterval(durationTimer);
      }
    };
  }, [unifiedCallState.status]); // Re-run when call status changes

  let options = {
    // Pass your App ID here.
    appId: null,
    // Set the channel name.
    channel: `Channel ${new Date().getTime()}`,
    // Pass your temp token here.
    token: null,
    // Set the user ID.
    uid: 0,
  };

  let channelParameters = {
    // A variable to hold a local audio track.
    localAudioTrack: null,
    // A variable to hold a remote audio track.
    localVideoTrack: null,

    remoteAudioTrack: null,
    // A variable to hold the remote user id.
    remoteVideoTrack: null,

    remoteUid: null,
  };

  // FILTER VARIABLES

  const [page_no, setPageNo] = useState("1");

  const [state, setState] = useState([]);
  const [speciality, setSpeciality] = useState([]);
  const [price, setPrice] = useState(39);
  const [online, setOnline] = useState(false);
  const [offline, setOffline] = useState(false);
  const [all, setAll] = useState(false);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [language, setLanguage] = useState([]);
  const [rating, setRating] = useState("");

  const audioChannel = new BroadcastChannel("audio_control");
  const receive_call_audio = new Audio(Ringtone);
  receive_call_audio.loop = true;

  const searchQuery = useRef(ConstConfig.MAIN_URL);
  const [submited_find_query, setSubmitedFindQuery] = useState(
    ConstConfig.MAIN_URL
  );

  // PAUSE AUDIO
  function pauseAudio() {
    receive_call_audio.pause();
    receive_call_audio.currentTime = 0;
    audioChannel.postMessage("pause");
    // Clear global reference
    if (window.activeRingtone) {
      window.activeRingtone = null;
    }
  }

  // NEW: Unified call state helper functions
  const updateCallState = (updates) => {
    setUnifiedCallState(prev => {
      const newState = {
        ...prev,
        ...updates,
        // Support nested updates (deep merge)
        user: updates.user ? { ...prev.user, ...updates.user } : prev.user,
        appointment: updates.appointment ? { ...prev.appointment, ...updates.appointment } : prev.appointment,
        tracks: updates.tracks ? { ...prev.tracks, ...updates.tracks } : prev.tracks,
        expiry: updates.expiry ? { ...prev.expiry, ...updates.expiry } : prev.expiry,
      };

      return newState;
    });
  };

  const resetCallState = () => {
    // Reset ref duration
    callDurationRef.current = 0;

    setUnifiedCallState({
      status: 'idle',
      type: null,
      role: null,
      chat_id: null,
      remoteUserId: null,
      user: { _id: null, first_name: '', last_name: '', profile_image: '' },
      appointment: { _id: null, start_date_time: null, end_date_time: null, session_time: null },
      startTime: null,
      duration: 0,
      message_id: null,
      isMinimized: false,
      isMuted: false,
      isVideoHidden: false,
      showMessage: '',
      tracks: { localAudio: null, localVideo: null, remoteAudio: null, remoteVideo: null },
      isLoading: false,
      isEnding: false,
      activeTabId: null,
      expiry: { warningShown: false, expiredNotified: false, isExpired: false },
    });
  };

  // Helper to check if in active call
  const isInActiveCall = () => {
    return ['active', 'connecting', 'ringing_out', 'ringing_in'].includes(unifiedCallState.status);
  };

  // Getter for call duration (reads from ref - no re-render)
  const getCallDuration = () => {
    return callDurationRef.current;
  };


  // DECRYPT DATA FUNCTION

  function decryptData(key) {
    const encryptedData = localStorage.getItem(key);

    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(
          encryptedData,
          process.env.REACT_APP_CRYPTO_KEY
        );
        const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
        return decryptedData;
      } catch (error) {
        console.error("Decryption failed:", error.message);
        return null;
      }
    }

    return null;
  }

  //  GET ACCESSTOKEN FUN

  const getAccessToken = () => {
    return user.access_token;
  };

  return (
    <Provider
      value={{
        user,
        setUser,
        getAccessToken,
        loading,
        setLoading,
        activeTab,
        setActiveTab,
        setAgoraMessage,
        agoraMessage,
        setAgoraNewMessage,
        agoraNewMessage,
        setSocket,
        socket,
        acceptThroughPopup,
        setAcceptThroughPopup,
        selectedChat_user,
        footer_ref,
        setAuthRole,
        authUserRole,
        state,
        setState,
        speciality,
        setSpeciality,
        price,
        setPrice,
        online,
        setOnline,
        offline,
        setOffline,
        all,
        setAll,
        name,
        setName,
        gender,
        setGender,
        language,
        setLanguage,
        rating,
        setRating,
        searchQuery,
        submited_find_query,
        setSubmitedFindQuery,
        page_no,
        setPageNo,
        app_js_intialLoad,
        setSelectedUser,
        selectedUser,
        sessionModal,
        setSessionModalShow,
        setAgoraAppId,
        agoraAppId,
        appleClientId,
        setAppleClientId,
        facebookAppId,
        setFacebookAppId,
        google_client_id,
        setGoogleClientId,
        audioChannel,
        pauseAudio,
        receive_call_audio,
        showTimezoneChangeAlert,
        setShowTimezoneChangeAlert,
        setRefreshAppointmentType,
        refreshAppointmentType,
        show_otp_modal,
        setShowOtpModal,
        banner_details,
        setBannerDetails,
        options,
        channelParameters,
        showOldLicenseModal,
        setShowOldLicenseModal,
        isInCall,
        setIsInCall,
        authLoading,
        isCallMinimized,
        setIsCallMinimized,
        callMetadata,
        setCallMetadata,
        endCallFunctionRef,
        minimizeFunctionRef,

        // NEW: Unified call state
        unifiedCallState,
        updateCallState,
        resetCallState,
        isInActiveCall,

        // Call duration getter (ref-based, no re-render)
        getCallDuration,
      }}
    >
      {children}
    </Provider>
  );
};

// Custom hook for components that need live call duration updates
// This hook creates a local state that syncs with the ref every second
// Only components using this hook will re-render, not the entire context
export const useCallDuration = () => {
  const { getCallDuration, unifiedCallState } = React.useContext(AuthContext);
  const [duration, setDuration] = React.useState(0);

  React.useEffect(() => {
    // Sync local duration with ref immediately
    setDuration(getCallDuration());

    // Only set up interval if call is active
    if (unifiedCallState.status === 'active') {
      const syncInterval = setInterval(() => {
        setDuration(getCallDuration());
      }, 1000);

      return () => clearInterval(syncInterval);
    }
  }, [getCallDuration, unifiedCallState.status]);

  return duration;
};

export { AuthProvider, AuthContext };
